/**
 * Hook to check whether the given email is on the free-access whitelist
 * (emails granted free app access without an active subscription).
 * Whitelist is managed from the admin panel and stored in Firestore
 * (`freeAccessWhitelist/{normalizedEmail}`), read directly here so it stays
 * live if an admin adds/removes an email while the app is open.
 *
 * Being on the list is not enough by itself: each entry also carries an
 * `accessStart`/`accessEnd` window (set by the admin — 30 days, a year, or a
 * custom range; both null means unlimited), so access is only granted while
 * the current time falls inside that window.
 */
import { useEffect, useState } from 'react';
import { doc, getFirestore, onSnapshot, Timestamp } from 'firebase/firestore';
import { firebaseApp } from './firebaseConfig';

const db = getFirestore(firebaseApp);

function isWithinAccessWindow(data: {
  accessStart?: Timestamp | null;
  accessEnd?: Timestamp | null;
}): boolean {
  const now = Date.now();
  if (data.accessStart && data.accessStart.toMillis() > now) return false;
  if (data.accessEnd && data.accessEnd.toMillis() < now) return false;
  return true;
}

export function useIsFreeAccessWhitelisted(email: string | null): boolean {
  const [isWhitelisted, setIsWhitelisted] = useState(false);

  useEffect(() => {
    if (!email) {
      setIsWhitelisted(false);
      return;
    }
    const normalized = email.trim().toLowerCase();
    const ref = doc(db, 'freeAccessWhitelist', normalized);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => setIsWhitelisted(snap.exists() && isWithinAccessWindow(snap.data() ?? {})),
      (err) => {
        console.warn('[FreeAccessWhitelist] listener error', err);
        setIsWhitelisted(false);
      },
    );
    return () => unsubscribe();
  }, [email]);

  return isWhitelisted;
}
